// The patient app's state machine (Frontend §4.5): one reducer; screens are pure functions of its state, effects
// (API calls) run in hooks that dispatch events. Task 05 states: boot, choose, newPatient, identify, notFound,
// lookupError, unavailable, visit, expired, unavailableClinic, notFoundPage, plus blocked (P-14 copy for `blocked` /
// `rate_limited`). Task 06: datetime (P-08), review (P-09), confirming (P-10), bookFailed (P-11 default), booked
// (P-12), and refetchingOptions (`option_unavailable` → P-07). Task 07: the P-08 banners (`datetime{banner}`), the
// struck-through "just taken" times (`dt.taken`), bookUnconfirmed (the P-11 outcome-unknown variant) and the rest of
// the history rules. Task 17: capReached (P-15, `booking_cap_reached` at lookup or confirm). Patient details (and the reason for visit) live in this state only (Frontend §4.4): never
// storage, URLs or logs.
import type { ApiErrorCode } from '@pa/api'
import type { BookingConfirmation, BookingStatus, PublicHold, PublicOptions } from '@pa/api/public'
import { EMPTY_FIELDS, type IdentifyFields } from '@/features/entry'
import { choose as chooseIn, isOneOneOne, presetChoice, resolve, type Choice } from './resolve'

export type { IdentifyFields }

/** A slot of the week (API.md `WeekAvailability.days[].slots[]`). */
export interface Slot {
  start: string
  end: string
}

/**
 * P-08 picks: the week shown (null = the window's first week), the day and the time; `reload` refetches the week.
 * `taken`: starts another patient just took (a poll dropped them, or a hold got `slot_taken`), drawn struck through
 * until the day or week changes or a new time is picked (task 07).
 */
export interface DateTimePick {
  weekStart: string | null
  day: string | null
  slot: Slot | null
  reload: number
  taken: readonly string[]
}

/**
 * Why the patient is back on P-08 (Frontend §4.5 `datetime{banner}`; copy in UserFlows P-08): the hold was refused
 * (`taken`), the P-09 countdown ran out (`holdRanOut`), or P-10 answered `hold_expired` / `slot_taken`.
 */
export type Banner = 'taken' | 'holdRanOut' | 'expiredAtConfirm' | 'takenAtConfirm'

/** P-09 reason: "Let me write the reason" (`WRITE_REASON`) or one of the clinic's common reasons. */
export const WRITE_REASON = '__write__'
export interface ReasonDraft {
  choice: string
  text: string
}

/** The booking steps' shared data (P-07 to P-12). */
export interface Flow {
  firstName: string
  options: PublicOptions
  choice: Choice
  /**
   * The link preset's parts still offered (task 22, D-79): the starting draft, and the fallback for parts not chosen
   * yet. Absent = no preset.
   */
  preferred?: Choice
  /** 1·1·1: P-07 was skipped (P-08 is "Step 1 of 2", with no Back). */
  skipped: boolean
  dt: DateTimePick
  reason: ReasonDraft
}

/** A placed hold, with the countdown anchor: `performance.now()` when the response arrived (Frontend §4.9). */
export interface HeldTime extends PublicHold {
  receivedAt: number
}

/** The reason as sent and shown on P-12: the written text or the chosen common reason; null = "Not provided". */
export function reasonOf(r: ReasonDraft): string | null {
  const value = (r.choice === WRITE_REASON ? r.text : r.choice).trim()
  return value === '' ? null : value
}

export type State =
  | { name: 'boot' }
  | { name: 'choose'; checking: boolean }
  | { name: 'newPatient' }
  /** P-03. `challenge`: P-13, the checkbox beside Continue after `challenge_required` (task 16). */
  | { name: 'identify'; pending: boolean; fields: IdentifyFields; formKey: number; challenge?: boolean }
  | { name: 'notFound' }
  | { name: 'lookupError'; fields: IdentifyFields }
  /** P-06. `canGoBack`: Back → P-01 (hidden with `existing=1` and after lookup). `contact`: config failed → none. */
  | { name: 'unavailable'; canGoBack: boolean; contact: boolean }
  /** Found: #6 is loading. */
  | { name: 'loadingOptions'; firstName: string }
  | { name: 'visit'; flow: Flow }
  /** P-08. `holding`: "Holding your time…" (#8 in flight). `banner`: why the patient is back here. */
  | { name: 'datetime'; flow: Flow; holding: boolean; banner: Banner | null }
  /** P-09 with the live hold. `challenge`: P-13 beside Confirm after a booking's `challenge_required` (task 16). */
  | { name: 'review'; flow: Flow; hold: HeldTime; challenge?: boolean }
  /** P-10: #10 in flight with one `Idempotency-Key` for this confirm attempt (Frontend §4.10). */
  | { name: 'confirming'; flow: Flow; hold: HeldTime; key: string }
  /** P-11 (default variant). */
  | { name: 'bookFailed'; flow: Flow }
  /** P-11 outcome-unknown variant (`booking_unconfirmed`, D-30): terminal, no "Try again", browser Back stays. */
  | { name: 'bookUnconfirmed'; flow: Flow }
  /** P-12: the confirmation plus the reason held in memory (the server doesn't echo it, D-22). */
  | { name: 'booked'; flow: Flow; confirmation: BookingConfirmation; reason: string | null }
  /** `option_unavailable` at #7 / #8: options are fetched again, then P-07. */
  | { name: 'refetchingOptions'; flow: Flow }
  | { name: 'expired' }
  | { name: 'unavailableClinic' }
  /** P-18 (task 22, D-79): the link's **locked** preset is no longer bookable online (#6 `preset.locked && stale`). */
  | { name: 'presetUnavailable' }
  | { name: 'blocked' }
  /** P-15 Booking limit reached (task 17): terminal, no actions; any hold is dropped client-side. */
  | { name: 'capReached' }
  | { name: 'notFoundPage' }
  /**
   * Embed only (task 08; Frontend §4.4 step 9, Architecture §6.4): the visit cookie never round-tripped (a browser
   * without partitioned cookies), so the panel offers "Continue in a new tab" instead of P-16.
   */
  | { name: 'cookiesBlocked' }

export interface Machine {
  state: State
  /** The link had `existing=1`: P-01 is skipped, and P-03 / P-06 have no Back. */
  existingOnly: boolean
  /** The `/c/{slug}/embed` iframe (task 08): `session_expired` before the cookie round-tripped → `cookiesBlocked`. */
  embed: boolean
  /** The patient was found in this boot: the embed shows everything from here on in the overlay (Frontend §4.5). */
  found: boolean
  /** A cookie-bearing call answered with something other than `session_expired`: the visit cookie round-trips. */
  cookieProven: boolean
  /** Bumped by Start again: a new boot (config + new session). */
  bootId: number
  formKey: number
}

export type Outcome<T> = { ok: true; value: T } | { ok: false; code: ApiErrorCode }

export type Event =
  | { type: 'BOOTED'; config: Outcome<BookingStatus>; session: Outcome<null> }
  | { type: 'CHOOSE_EXISTING' }
  | { type: 'RECHECKED'; config: Outcome<BookingStatus> }
  | { type: 'CHOOSE_NEW' }
  | { type: 'BACK' }
  | { type: 'LOOKUP_START'; fields: IdentifyFields }
  | { type: 'LOOKUP_FOUND'; firstName: string }
  | { type: 'LOOKUP_NOT_FOUND' }
  | { type: 'LOOKUP_FAILED'; code: ApiErrorCode }
  | { type: 'OPTIONS_LOADED'; options: PublicOptions }
  | { type: 'OPTIONS_FAILED'; code: ApiErrorCode }
  | { type: 'TRY_AGAIN' }
  | { type: 'CHOOSE'; patch: Partial<Choice> }
  | { type: 'CHOOSE_TIME' }
  | { type: 'START_AGAIN' }
  // ---- task 06: P-08 to P-12
  | { type: 'WEEK'; weekStart: string | null }
  | { type: 'PICK_DAY'; day: string }
  | { type: 'PICK_SLOT'; slot: Slot }
  /** #7 failed in a way that leaves P-08. */
  | { type: 'WEEK_FAILED'; code: ApiErrorCode }
  | { type: 'HOLD_START' }
  | { type: 'HOLD_OK'; hold: PublicHold; receivedAt: number }
  | { type: 'HOLD_FAILED'; code: ApiErrorCode }
  /** P-09 Back / Change (the hold is released; the time stays selected). */
  | { type: 'CHANGE' }
  /** P-09 countdown reached 0 (the hold is released; the time is cleared). */
  | { type: 'HOLD_EXPIRED' }
  | { type: 'REASON'; patch: Partial<ReasonDraft> }
  | { type: 'CONFIRM'; key: string }
  | { type: 'BOOKED'; confirmation: BookingConfirmation }
  | { type: 'BOOK_FAILED'; code: ApiErrorCode }
  | { type: 'OPTIONS_REFETCHED'; options: PublicOptions }
  // ---- task 07
  /** A 20 s poll (or a reload) no longer has these starts of the picked day (API.md §4.7 "Live updates"). */
  | { type: 'SLOTS_GONE'; starts: readonly string[] }
  /**
   * The booking of an attempt the patient left with browser Back (P-10 → P-08) answered after all: 201 → P-12, an
   * unknown outcome → the P-11 variant. Any other answer is ignored (the server released the hold).
   */
  | { type: 'LATE_BOOKED'; confirmation: BookingConfirmation; reason: string | null }
  | { type: 'LATE_UNCONFIRMED' }
  /** Browser Back / Forward landed on a booking step (Frontend §4.7). */
  | { type: 'POP'; step: BookingStep }
  // ---- task 16
  /** P-13's checkbox widget failed to load, and its one retry too → P-14 (UserFlows P-13). */
  | { type: 'CHALLENGE_UNAVAILABLE' }

/** The booking steps that have browser history entries (Frontend §4.7). */
export type BookingStep = 'P-07' | 'P-08' | 'P-09' | 'P-10' | 'P-11' | 'P-12'

/** The history step of a state, if it is a booking step. */
export function stepOf(s: State): BookingStep | undefined {
  switch (s.name) {
    case 'visit':
      return 'P-07'
    case 'datetime':
      return 'P-08'
    case 'review':
      return 'P-09'
    case 'confirming':
      return 'P-10'
    case 'bookFailed':
    case 'bookUnconfirmed':
      return 'P-11'
    case 'booked':
      return 'P-12'
    default:
      return undefined
  }
}

export function initialMachine(existingOnly: boolean, embed = false): Machine {
  return {
    state: { name: 'boot' },
    // The embed ignores `existing=1`: it always asks (Frontend §4.1, UserFlows §2.1).
    existingOnly: existingOnly && !embed,
    embed,
    found: false,
    cookieProven: false,
    bootId: 0,
    formKey: 0,
  }
}

/** Lookup answers that don't prove the cookie round-tripped (no answer, or the cookie was missing). */
const NO_COOKIE_PROOF: ReadonlySet<ApiErrorCode> = new Set<ApiErrorCode>([
  'session_expired',
  'network_error',
  'timeout',
])

const identify = (m: Machine, fields: IdentifyFields = EMPTY_FIELDS): State => ({
  name: 'identify',
  pending: false,
  fields,
  formKey: m.formKey,
})

const isBlocked = (code: ApiErrorCode) => code === 'blocked' || code === 'rate_limited'

/** Where an opening config `bookingStatus` sends the patient (Frontend §4.5 `boot`, API.md §4.1). */
function afterStatus(m: Machine, status: BookingStatus, canGoBack: boolean): State {
  if (status === 'clinic_inactive' || status === 'nothing_bookable') return { name: 'unavailableClinic' }
  if (status === 'pf_unavailable') return { name: 'unavailable', canGoBack, contact: true }
  return identify(m)
}

function onBooted(m: Machine, e: Extract<Event, { type: 'BOOTED' }>): State {
  const { config, session } = e
  if ((!config.ok && config.code === 'not_found') || (!session.ok && session.code === 'not_found')) {
    return { name: 'notFoundPage' }
  }
  if ((!config.ok && isBlocked(config.code)) || (!session.ok && isBlocked(session.code))) return { name: 'blocked' }
  // `internal_error` (or a network failure) on #1 → P-06 content without the contact block (Frontend §3.5).
  if (!config.ok) return { name: 'unavailable', canGoBack: false, contact: false }
  if (config.value === 'clinic_inactive' || config.value === 'nothing_bookable') return { name: 'unavailableClinic' }
  // #2 failed (500, invalid_request, network) → P-06 content (T-P01-10).
  if (!session.ok) return { name: 'unavailable', canGoBack: false, contact: true }
  if (m.existingOnly) return afterStatus(m, config.value, false)
  return { name: 'choose', checking: false }
}

/** Lookup failures (Frontend §3.5 / §4.5 `identify`). */
function onLookupFailed(m: Machine, code: ApiErrorCode, fields: IdentifyFields): State {
  switch (code) {
    case 'invalid_request':
      return { name: 'notFound' }
    case 'pf_unavailable':
      return { name: 'unavailable', canGoBack: !m.existingOnly, contact: true }
    case 'session_expired':
      return { name: 'expired' }
    case 'clinic_inactive':
      return { name: 'unavailableClinic' }
    // P-13 (task 16): the checkbox beside Continue; the typed values stay.
    case 'challenge_required':
      return { name: 'identify', pending: false, fields, formKey: m.formKey, challenge: true }
    case 'blocked':
    case 'rate_limited':
      return { name: 'blocked' }
    // P-15 straight after the lookup (D-33): no patient session was issued.
    case 'booking_cap_reached':
      return { name: 'capReached' }
    default:
      // pf_error, internal_error, network_error, timeout → P-05 with the fields kept.
      return { name: 'lookupError', fields }
  }
}

/** Options (#6) failures: never `pf_unavailable` from the server (D-62), but mapped like any other step. */
function onOptionsFailed(code: ApiErrorCode): State {
  if (code === 'session_expired') return { name: 'expired' }
  if (code === 'clinic_inactive') return { name: 'unavailableClinic' }
  if (isBlocked(code)) return { name: 'blocked' }
  // pf_unavailable, 5xx, network → P-06 content, entered after lookup: no Back (T-P07-13, T-P07-15).
  return { name: 'unavailable', canGoBack: false, contact: true }
}

export function reduce(m: Machine, e: Event): Machine {
  const s = m.state
  const to = (state: State): Machine => ({ ...m, state })
  switch (e.type) {
    case 'BOOTED':
      return s.name === 'boot' ? to(onBooted(m, e)) : m
    case 'CHOOSE_EXISTING':
      return s.name === 'choose' && !s.checking ? to({ name: 'choose', checking: true }) : m
    case 'RECHECKED':
      if (s.name !== 'choose') return m
      if (!e.config.ok) {
        return to(
          isBlocked(e.config.code) ? { name: 'blocked' } : { name: 'unavailable', canGoBack: true, contact: true },
        )
      }
      return to(afterStatus(m, e.config.value, true))
    case 'CHOOSE_NEW':
      return s.name === 'choose' && !s.checking ? to({ name: 'newPatient' }) : m
    case 'BACK':
      if (s.name === 'newPatient') return to({ name: 'choose', checking: false })
      if (s.name === 'identify' && !s.pending && !m.existingOnly) return to({ name: 'choose', checking: false })
      if (s.name === 'unavailable' && s.canGoBack) return to({ name: 'choose', checking: false })
      if (s.name === 'datetime' && !s.holding && !s.flow.skipped) return to({ name: 'visit', flow: s.flow })
      return m
    case 'LOOKUP_START':
      return s.name === 'identify' && !s.pending ? to({ ...s, pending: true, fields: e.fields }) : m
    case 'LOOKUP_FOUND':
      return s.name === 'identify' && s.pending
        ? { ...m, found: true, cookieProven: true, state: { name: 'loadingOptions', firstName: e.firstName } }
        : m
    case 'LOOKUP_NOT_FOUND':
      return s.name === 'identify' && s.pending ? { ...m, cookieProven: true, state: { name: 'notFound' } } : m
    case 'LOOKUP_FAILED':
      if (s.name !== 'identify' || !s.pending) return m
      // Embed: `session_expired` on the first cookie-bearing call means the cookie never round-tripped (Frontend
      // §4.4 step 9). On the direct link, or once the cookie has worked, it is P-16.
      if (e.code === 'session_expired' && m.embed && !m.cookieProven) return to({ name: 'cookiesBlocked' })
      return {
        ...m,
        cookieProven: m.cookieProven || !NO_COOKIE_PROOF.has(e.code),
        state: onLookupFailed(m, e.code, s.fields),
      }
    case 'OPTIONS_LOADED':
      if (s.name !== 'loadingOptions') return m
      // A stale locked preset → P-18, not P-17 (UserFlows P-18, T-P07-23).
      if (isStaleLock(e.options)) return to({ name: 'presetUnavailable' })
      // Empty `combos` → P-17 (UserFlows P-17, T-P07-12).
      if (e.options.combos.length === 0) return to({ name: 'unavailableClinic' })
      return to(startFlow(s.firstName, e.options))
    case 'OPTIONS_FAILED':
      return s.name === 'loadingOptions' ? to(onOptionsFailed(e.code)) : m
    case 'TRY_AGAIN': {
      // P-04: empty fields (a fresh form). P-05: the fields are kept.
      if (s.name === 'notFound') {
        const formKey = m.formKey + 1
        return { ...m, formKey, state: { name: 'identify', pending: false, fields: EMPTY_FIELDS, formKey } }
      }
      if (s.name === 'lookupError') return to(identify(m, s.fields))
      return reduceBooking(m, e)
    }
    case 'CHOOSE': {
      if (s.name !== 'visit') return m
      const choice = chooseIn(s.flow.options, s.flow.choice, e.patch, s.flow.preferred)
      const same =
        choice.locationId === s.flow.choice.locationId &&
        choice.visitTypeId === s.flow.choice.visitTypeId &&
        choice.providerId === s.flow.choice.providerId
      // Changing an earlier choice also clears any chosen day and time (UserFlows P-07).
      return to({ name: 'visit', flow: { ...s.flow, choice, dt: same ? s.flow.dt : NO_PICK } })
    }
    case 'CHOOSE_TIME':
      return s.name === 'visit' &&
        s.flow.choice.locationId !== null &&
        s.flow.choice.visitTypeId !== null &&
        s.flow.choice.providerId !== null
        ? to(dateTime(s.flow))
        : m
    case 'START_AGAIN':
      return s.name === 'expired'
        ? { ...m, bootId: m.bootId + 1, formKey: m.formKey + 1, found: false, state: { name: 'boot' } }
        : m
    default:
      return reduceBooking(m, e)
  }
}

const NO_PICK: DateTimePick = { weekStart: null, day: null, slot: null, reload: 0, taken: [] }

/** P-08 with no banner and nothing in flight. */
const dateTime = (flow: Flow, banner: Banner | null = null): State => ({
  name: 'datetime',
  flow,
  holding: false,
  banner,
})

/** #6 says the link's locked preset is no longer bookable online (P-18). */
const isStaleLock = (o: PublicOptions) => o.preset?.locked === true && o.preset.stale

/**
 * Found + options: P-07, or P-08 when the step is skipped (1·1·1, UserFlows P-07). The link preset's parts still
 * offered are preselected (task 22); the normal skip logic still applies.
 */
function startFlow(firstName: string, options: PublicOptions): State {
  const preferred = presetChoice(options)
  const choice = resolve(options, preferred)
  const skipped = isOneOneOne(options)
  const flow: Flow = {
    firstName,
    options,
    choice,
    preferred,
    skipped,
    dt: NO_PICK,
    reason: { choice: WRITE_REASON, text: '' },
  }
  return skipped ? dateTime(flow) : { name: 'visit', flow }
}

/**
 * P-08 again: the day kept, the time cleared and the week fetched again, with the banner that explains why
 * (`struck`: the lost time is drawn struck through, UserFlows P-08 "Hold could not be placed").
 */
const backToDay = (flow: Flow, banner: Banner | null = null, struck = false): State => {
  const lost = flow.dt.slot?.start
  const taken = struck && lost !== undefined && !flow.dt.taken.includes(lost) ? [...flow.dt.taken, lost] : flow.dt.taken
  return dateTime({ ...flow, dt: { ...flow.dt, slot: null, taken, reload: flow.dt.reload + 1 } }, banner)
}

/** Codes that leave the booking steps whatever the step (Frontend §3.5). */
function leaveFlow(code: ApiErrorCode): State | undefined {
  switch (code) {
    case 'pf_unavailable':
      // P-06 content, entered after lookup: no Back.
      return { name: 'unavailable', canGoBack: false, contact: true }
    case 'session_expired':
      return { name: 'expired' }
    case 'clinic_inactive':
      return { name: 'unavailableClinic' }
    case 'blocked':
    case 'rate_limited':
      return { name: 'blocked' }
    default:
      return undefined
  }
}

/**
 * #7 failures. `invalid_request` (a client bug, e.g. a `weekStart` that isn't a bookable Sunday) reloads the first
 * week in place (Frontend §3.5, T-P08-33); if the first week itself is refused, P-06 content.
 */
function onWeekFailed(flow: Flow, code: ApiErrorCode): State {
  if (code === 'invalid_request' && flow.dt.weekStart !== null) {
    return dateTime({ ...flow, dt: { ...NO_PICK, reload: flow.dt.reload + 1 } })
  }
  if (code === 'conflict') return { name: 'refetchingOptions', flow }
  // 500, network and anything else → P-06 content (API.md "other" 5xx).
  return leaveFlow(code) ?? { name: 'unavailable', canGoBack: false, contact: true }
}

/** #8 failures (Frontend §3.5, §4.5 `datetime{banner}`). */
function onHoldFailed(flow: Flow, code: ApiErrorCode): State {
  if (code === 'conflict') return { name: 'refetchingOptions', flow }
  const out = leaveFlow(code)
  if (out) return out
  if (
    code === 'internal_error' ||
    code === 'network_error' ||
    code === 'timeout' ||
    // D-47: still refused after the one retry; a toast says "You already have a booking in progress…".
    code === 'request_in_progress'
  ) {
    // Stay on P-08 with the time kept (a toast explains).
    return dateTime(flow)
  }
  if (code === 'invalid_request') {
    return dateTime({ ...flow, dt: { ...NO_PICK, reload: flow.dt.reload + 1 } })
  }
  // slot_taken, any other 409 (patient fallback → P-08): "That time was just taken", the time struck through.
  return backToDay(flow, 'taken', true)
}

/**
 * Codes that leave the outcome of a confirm unknown (D-30, Security.md §7.5): the server's `booking_unconfirmed`, and
 * a lost answer still unresolved after the client's one replay. Never "Try again", which could book twice.
 */
const UNKNOWN_OUTCOME: ReadonlySet<ApiErrorCode> = new Set<ApiErrorCode>([
  'booking_unconfirmed',
  'network_error',
  'timeout',
  'invalid_response',
  'request_in_progress',
])

/** #10 failures (Frontend §3.5, §4.5 `confirming`). */
function onBookFailed(flow: Flow, code: ApiErrorCode): State {
  if (code === 'hold_expired') return backToDay(flow, 'expiredAtConfirm')
  if (code === 'slot_taken') return backToDay(flow, 'takenAtConfirm')
  if (UNKNOWN_OUTCOME.has(code)) return { name: 'bookUnconfirmed', flow }
  const out = leaveFlow(code)
  if (out) return out
  // P-15 (task 17): the server released the hold; the client drops it too. `challenge_required` is P-13 (BOOK_FAILED
  // below).
  if (code === 'booking_cap_reached') return { name: 'capReached' }
  // option_unavailable (409 conflict), pf_error, internal_error, invalid_request, idempotency_key_reused → P-11.
  return { name: 'bookFailed', flow }
}

/** Adds `starts` to the struck-through times; clears the selection when it is one of them. */
function onSlotsGone(flow: Flow, starts: readonly string[]): Flow {
  const taken = [...flow.dt.taken, ...starts.filter((st) => !flow.dt.taken.includes(st))]
  const slot = flow.dt.slot && starts.includes(flow.dt.slot.start) ? null : flow.dt.slot
  return { ...flow, dt: { ...flow.dt, taken, slot } }
}

function reduceBooking(m: Machine, e: Event): Machine {
  const s = m.state
  const to = (state: State): Machine => ({ ...m, state })
  switch (e.type) {
    case 'WEEK':
      return s.name === 'datetime' && !s.holding
        ? to({ ...s, flow: { ...s.flow, dt: { ...s.flow.dt, weekStart: e.weekStart, taken: [] } } })
        : m
    case 'PICK_DAY':
      return s.name === 'datetime' && !s.holding
        ? to({ ...s, flow: { ...s.flow, dt: { ...s.flow.dt, day: e.day, slot: null, taken: [] } } })
        : m
    case 'PICK_SLOT':
      // A struck-through time can't be picked (Architecture P-04). A new time clears the banner and the struck times.
      return s.name === 'datetime' && !s.holding && !s.flow.dt.taken.includes(e.slot.start)
        ? to({ ...s, banner: null, flow: { ...s.flow, dt: { ...s.flow.dt, slot: e.slot, taken: [] } } })
        : m
    case 'SLOTS_GONE':
      return s.name === 'datetime' && !s.holding && e.starts.length > 0
        ? to({ ...s, flow: onSlotsGone(s.flow, e.starts) })
        : m
    case 'WEEK_FAILED':
      return s.name === 'datetime' ? to(onWeekFailed(s.flow, e.code)) : m
    case 'HOLD_START':
      return s.name === 'datetime' && !s.holding && s.flow.dt.slot !== null ? to({ ...s, holding: true }) : m
    case 'HOLD_OK':
      return s.name === 'datetime' && s.holding
        ? to({ name: 'review', flow: s.flow, hold: { ...e.hold, receivedAt: e.receivedAt } })
        : m
    case 'HOLD_FAILED':
      return s.name === 'datetime' && s.holding ? to(onHoldFailed(s.flow, e.code)) : m
    case 'CHANGE':
      return s.name === 'review' ? to(dateTime(s.flow)) : m
    case 'HOLD_EXPIRED':
      return s.name === 'review' ? to(backToDay(s.flow, 'holdRanOut')) : m
    case 'REASON':
      return s.name === 'review' ? to({ ...s, flow: { ...s.flow, reason: { ...s.flow.reason, ...e.patch } } }) : m
    case 'CONFIRM':
      return s.name === 'review' ? to({ name: 'confirming', flow: s.flow, hold: s.hold, key: e.key }) : m
    case 'BOOKED':
      return s.name === 'confirming'
        ? to({ name: 'booked', flow: s.flow, confirmation: e.confirmation, reason: reasonOf(s.flow.reason) })
        : m
    case 'BOOK_FAILED':
      if (s.name !== 'confirming') return m
      // P-13 beside Confirm (task 16): back to P-09 with the same hold; its countdown keeps running.
      if (e.code === 'challenge_required') return to({ name: 'review', flow: s.flow, hold: s.hold, challenge: true })
      return to(onBookFailed(s.flow, e.code))
    case 'CHALLENGE_UNAVAILABLE':
      return (s.name === 'identify' && s.challenge === true) || (s.name === 'review' && s.challenge === true)
        ? to({ name: 'blocked' })
        : m
    case 'TRY_AGAIN':
      return s.name === 'bookFailed' ? to(backToDay(s.flow)) : m
    case 'LATE_BOOKED':
      return lateTarget(s) ? to({ name: 'booked', flow: s.flow, confirmation: e.confirmation, reason: e.reason }) : m
    case 'LATE_UNCONFIRMED':
      return lateTarget(s) ? to({ name: 'bookUnconfirmed', flow: s.flow }) : m
    case 'OPTIONS_REFETCHED': {
      if (s.name !== 'refetchingOptions') return m
      if (isStaleLock(e.options)) return to({ name: 'presetUnavailable' })
      if (e.options.combos.length === 0) return to({ name: 'unavailableClinic' })
      // Choices that no longer fit are cleared by resolve (Frontend §3.5); the day and time are dropped.
      const choice = resolve(e.options, s.flow.choice)
      const preferred = presetChoice(e.options)
      return to({
        name: 'visit',
        flow: { ...s.flow, options: e.options, choice, preferred, skipped: false, dt: NO_PICK },
      })
    }
    case 'POP':
      return to(onPop(s, e.step))
    default:
      return m
  }
}

/** A booking-flow state a late booking answer may still replace (not a terminal or leaving state). */
function lateTarget(s: State): s is Extract<State, { name: 'datetime' | 'review' | 'visit' | 'bookFailed' }> {
  return s.name === 'datetime' || s.name === 'review' || s.name === 'visit' || s.name === 'bookFailed'
}

/**
 * Browser Back / Forward onto a booking step (Frontend §4.7, UserFlows §2.4): P-12 and the P-11 outcome-unknown
 * variant stay; P-10 and P-11 go to P-08 (the day kept, the time cleared); P-09 needs a live hold, else P-08. Leaving
 * P-09 keeps the chosen time (as Back / Change does).
 */
function onPop(s: State, step: BookingStep): State {
  if (s.name === 'booked' || s.name === 'bookUnconfirmed') return s
  if (!('flow' in s) || s.name === 'refetchingOptions') return s
  const flow = s.flow
  // P-10 (the booking may still be running; its late answer is handled by LATE_*) or P-11 → P-08.
  if (s.name === 'confirming' || s.name === 'bookFailed') return backToDay(flow)
  switch (step) {
    case 'P-07':
      return flow.skipped ? (s.name === 'datetime' ? s : dateTime(flow)) : { name: 'visit', flow }
    case 'P-09':
      return s.name === 'review' ? s : s.name === 'datetime' ? s : dateTime(flow)
    default:
      return s.name === 'datetime' ? s : dateTime(flow)
  }
}
